/**
 * MidiStatus Component
 * --------------------
 * Studio Console Design: Compact status bar with LED-style indicators
 */

import { motion } from "framer-motion";
import { Usb, Unplug, Music2 } from "lucide-react";
import type { MidiDevice } from "@/hooks/useMidi";

interface MidiStatusProps {
  isSupported: boolean;
  isConnected: boolean;
  activeDevice: MidiDevice | null;
  devices: MidiDevice[];
  activeNotes: Set<number>;
  onDeviceSelect: (id: string) => void;
  error: string | null;
}

export default function MidiStatus({
  isSupported,
  isConnected,
  activeDevice,
  devices,
  activeNotes,
  onDeviceSelect,
  error,
}: MidiStatusProps) {
  return (
    <div className="flex items-center gap-3 text-xs font-mono">
      {/* Connection LED */}
      <div className="flex items-center gap-1.5">
        <motion.div
          className="w-2 h-2 rounded-full"
          style={{
            backgroundColor: isConnected
              ? "oklch(0.82 0.17 75)"
              : error
              ? "oklch(0.65 0.24 25)"
              : "oklch(0.40 0.01 270)",
          }}
          animate={
            isConnected
              ? { boxShadow: ["0 0 4px oklch(0.82 0.17 75 / 60%)", "0 0 8px oklch(0.82 0.17 75 / 30%)", "0 0 4px oklch(0.82 0.17 75 / 60%)"] }
              : {}
          }
          transition={{ duration: 2, repeat: Infinity }}
        />
        {isConnected ? (
          <Usb size={12} style={{ color: "oklch(0.82 0.17 75)" }} />
        ) : (
          <Unplug size={12} style={{ color: "oklch(0.50 0.01 270)" }} />
        )}
      </div>

      {/* Device name or error */}
      {error ? (
        <span style={{ color: "oklch(0.65 0.24 25)" }} className="truncate max-w-[200px]">
          {error}
        </span>
      ) : isConnected && activeDevice ? (
        <span style={{ color: "oklch(0.70 0.01 270)" }} className="truncate max-w-[200px]">
          {activeDevice.name}
        </span>
      ) : !isSupported ? (
        <span style={{ color: "oklch(0.50 0.01 270)" }}>
          Web MIDI not supported — use Chrome/Edge
        </span>
      ) : devices.length === 0 ? (
        <span style={{ color: "oklch(0.50 0.01 270)" }}>
          No MIDI device — connect keyboard or use mouse
        </span>
      ) : (
        <select
          className="bg-transparent border border-border rounded px-1 py-0.5 text-xs"
          style={{ color: "oklch(0.70 0.01 270)" }}
          onChange={(e) => onDeviceSelect(e.target.value)}
          defaultValue=""
        >
          <option value="" disabled>
            Select device...
          </option>
          {devices.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      )}

      {/* Activity indicator */}
      {activeNotes.size > 0 && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="flex items-center gap-1"
        >
          <Music2 size={11} style={{ color: "oklch(0.82 0.17 75)" }} />
          <span style={{ color: "oklch(0.82 0.17 75)" }}>{activeNotes.size}</span>
        </motion.div>
      )}
    </div>
  );
}
